import {
  Badge,
  Checkbox,
  DescriptionList,
  LinkButton,
  SettingsSection,
  Stack,
  Text,
  useToast,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import { Link } from '@tanstack/react-router';
import { forwardRef, useId, useState, type AnchorHTMLAttributes } from 'react';
import { useSaveRoles, type AdminSettings, type SettingsUpdate } from '../../api/queries/settings';
import { withReference } from '../../api/withReference';
import { ROLE_LABELS } from '../users/UserModal';

type OptionalRole = AdminSettings['optionalRoles'][number];
type Role = OptionalRole['role'];
type Switchable = NonNullable<SettingsUpdate['enabledRoles']>[number];

/** What each optional role is for, in a line (ADR 0015, ADR 0028). */
const DESCRIPTIONS: Partial<Record<Role, string>> = {
  rep_intern: 'A rep who sees their pay history but not pending pay.',
  pay_viewer: 'Views any rep’s or supervisor’s pay, with no other admin access.',
};

/** Why a held role can't be switched off. The API's 409 says the same. */
export function heldMessage(role: Role, holders: number) {
  const people = holders === 1 ? '1 user holds' : `${holders} users hold`;
  return `${people} the ${ROLE_LABELS[role]} role. Change their roles to switch it off.`;
}

/** Users, filtered to one role's holders: a router link for LinkButton's `as`. */
function holdersLink(role: Role) {
  return forwardRef<HTMLAnchorElement, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'target'>>(
    function HoldersLink({ href: _href, ...rest }, ref) {
      return <Link ref={ref} to="/users" search={{ role }} {...rest} />;
    },
  );
}
const HOLDERS_LINKS = {
  rep_intern: holdersLink('rep_intern'),
  pay_viewer: holdersLink('pay_viewer'),
} as Record<Role, ReturnType<typeof holdersLink>>;

function Holders({ setting }: { setting: OptionalRole }) {
  if (setting.holderCount === 0) return null;
  return (
    <LinkButton as={HOLDERS_LINKS[setting.role]} href="/users">
      {setting.holderCount === 1 ? '1 user' : `${setting.holderCount} users`}
    </LinkButton>
  );
}

function RoleCheckbox({
  setting,
  checked,
  onChange,
}: {
  setting: OptionalRole;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  // A role anyone holds can't be switched off; it can always be switched on.
  const locked = checked && setting.enabled && setting.holderCount > 0;
  return (
    <Stack gap={1}>
      <Checkbox
        label={ROLE_LABELS[setting.role]}
        checked={checked}
        disabled={locked}
        aria-describedby={`${id}about${locked ? ` ${id}held` : ''}`}
        onChange={(event) => onChange(event.target.checked)}
      />
      <Text id={`${id}about`} size="meta" tone="muted">
        {DESCRIPTIONS[setting.role]}
      </Text>
      {locked && (
        <Text id={`${id}held`} size="meta">
          {heldMessage(setting.role, setting.holderCount)} <Holders setting={setting} />
        </Text>
      )}
    </Stack>
  );
}

/**
 * The optional roles (ADR 0028): Rep (intern) and Pay viewer, each switched on or off.
 * Admin, Supervisor and Rep are always on. A switched-off role isn't offered anywhere.
 */
export function RolesSection({ settings }: { settings: AdminSettings }) {
  const save = useSaveRoles();
  const { toast } = useToast();
  const initial = () =>
    Object.fromEntries(settings.optionalRoles.map((r) => [r.role, r.enabled])) as Record<
      Role,
      boolean
    >;
  const [draft, setDraft] = useState(initial);
  const enabledRoles = settings.optionalRoles
    .map((r) => r.role)
    .filter((role) => draft[role]) as Switchable[];
  const dirty = settings.optionalRoles.some((r) => draft[r.role] !== r.enabled);

  const items: DescriptionListItem[] = settings.optionalRoles.map((setting) => ({
    term: ROLE_LABELS[setting.role],
    description: (
      <Stack gap={1}>
        {setting.enabled ? (
          <Badge tone="success" dot>
            On
          </Badge>
        ) : (
          <Badge tone="neutral">Off</Badge>
        )}
        <Text size="meta" tone="muted">
          {DESCRIPTIONS[setting.role]} <Holders setting={setting} />
        </Text>
      </Stack>
    ),
  }));

  return (
    <SettingsSection
      title="Roles"
      description="Switch off the roles this workspace doesn’t use, so they aren’t offered. Admin, Supervisor and Rep are always on."
      mode="form"
      editLabel="Edit roles"
      onEditingChange={(editing) => {
        if (editing) setDraft(initial());
      }}
      dirty={dirty}
      onSave={() =>
        save.mutateAsync(enabledRoles).then(
          () => {
            toast({ tone: 'success', title: 'Roles saved' });
          },
          (error: unknown) => {
            toast(withReference({ tone: 'danger', title: 'Roles couldn’t be saved' }, error));
            throw new Error(reasonOfRoles(error));
          },
        )
      }
      form={
        <Stack gap={3}>
          {settings.optionalRoles.map((setting) => (
            <RoleCheckbox
              key={setting.role}
              setting={setting}
              checked={draft[setting.role]}
              onChange={(checked) => setDraft({ ...draft, [setting.role]: checked })}
            />
          ))}
        </Stack>
      }
    >
      <DescriptionList items={items} />
    </SettingsSection>
  );
}

function reasonOfRoles(error: unknown) {
  if (error && typeof error === 'object' && 'problem' in error) {
    const problem = (error as { problem: { detail?: string | null; title: string } }).problem;
    return problem.detail ?? problem.title;
  }
  return 'Check your connection and try again.';
}
